import React, { useState, useRef } from "react";
import { FiX, FiUpload, FiUser, FiMail, FiTag } from "react-icons/fi";
import { FaGoogle } from "react-icons/fa";

// Type-safe icon wrappers
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const UploadIcon = FiUpload as React.FC<React.SVGProps<SVGSVGElement>>;
const UserIcon = FiUser as React.FC<React.SVGProps<SVGSVGElement>>;
const MailIcon = FiMail as React.FC<React.SVGProps<SVGSVGElement>>;
const TagIcon = FiTag as React.FC<React.SVGProps<SVGSVGElement>>;
const GoogleIcon = FaGoogle as React.FC<React.SVGProps<SVGSVGElement>>;

interface AddContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCSVUpload: (file: File) => void;
  onGoogleImport: () => void;
  onAddManual: (contact: { name: string; email: string; type: string }) => void;
  existingTypes: string[];
}

/**
 * AddContactModal - Modal for importing/adding contacts
 */
const AddContactModal: React.FC<AddContactModalProps> = ({
  isOpen,
  onClose,
  onCSVUpload,
  onGoogleImport,
  onAddManual,
  existingTypes,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [type, setType] = useState("INVESTOR");
  const [customType, setCustomType] = useState("");
  const [showCustomType, setShowCustomType] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string; type?: string }>({});

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors: { name?: string; email?: string; type?: string } = {};
    
    if (!name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Invalid email format";
    }

    const finalType = showCustomType ? customType.trim().toUpperCase() : type;
    if (!finalType) {
      newErrors.type = "Type is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      const finalType = showCustomType ? customType.trim().toUpperCase() : type;
      await onAddManual({ name: name.trim(), email: email.trim(), type: finalType });
      // Reset form
      setName("");
      setEmail("");
      setType("INVESTOR");
      setCustomType("");
      setShowCustomType(false);
      setErrors({});
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.name.endsWith(".csv")) {
      onCSVUpload(file);
    } else {
      alert("Please select a valid CSV file");
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleTypeChange = (value: string) => {
    if (value === "CUSTOM") {
      setShowCustomType(true);
      setType("");
    } else {
      setShowCustomType(false);
      setType(value);
    }
  };

  return (
    <div className="su-modal-overlay" onClick={onClose}>
      <div
        className="su-modal su-add-contact-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="su-modal-header">
          <h2 className="su-modal-title">IMPORT CONTACTS</h2>
          <button className="su-modal-close" onClick={onClose}>
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="su-modal-body">
          {/* Import Buttons */}
          <div className="su-import-buttons">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <button
              className="su-import-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadIcon className="w-5 h-5" />
              <span>CSV FILE</span>
            </button>
            <button className="su-import-btn" onClick={onGoogleImport}>
              <GoogleIcon className="w-5 h-5" />
              <span>GOOGLE CONTACTS</span>
            </button>
          </div>

          {/* Divider */}
          <div className="su-modal-divider">
            <span>OR ADD MANUALLY</span>
          </div>

          {/* Manual Add Form */}
          <form onSubmit={handleSubmit} className="su-manual-form">
            {/* Name */}
            <div className="su-form-group">
              <label className="su-form-label">
                <UserIcon className="w-4 h-4" />
                <span>Name *</span>
              </label>
              <input
                type="text"
                className={`su-form-input ${errors.name ? "error" : ""}`}
                placeholder="Contact name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {errors.name && <span className="su-form-error">{errors.name}</span>}
            </div>

            {/* Email */}
            <div className="su-form-group">
              <label className="su-form-label">
                <MailIcon className="w-4 h-4" />
                <span>Email *</span>
              </label>
              <input
                type="email"
                className={`su-form-input ${errors.email ? "error" : ""}`}
                placeholder="contact@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {errors.email && <span className="su-form-error">{errors.email}</span>}
            </div>

            {/* Type */}
            <div className="su-form-group">
              <label className="su-form-label">
                <TagIcon className="w-4 h-4" />
                <span>Type *</span>
              </label>
              {!showCustomType ? (
                <select
                  className={`su-form-select ${errors.type ? "error" : ""}`}
                  value={type}
                  onChange={(e) => handleTypeChange(e.target.value)}
                >
                  {existingTypes.map((t) => (
                    <option key={t} value={t}>
                      {t.charAt(0) + t.slice(1).toLowerCase()}
                    </option>
                  ))}
                  <option value="CUSTOM">+ Add Custom Type</option>
                </select>
              ) : (
                <div className="su-custom-type-input">
                  <input
                    type="text"
                    className={`su-form-input ${errors.type ? "error" : ""}`}
                    placeholder="Enter custom type"
                    value={customType}
                    onChange={(e) => setCustomType(e.target.value)}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="su-custom-type-cancel"
                    onClick={() => {
                      setShowCustomType(false);
                      setType("INVESTOR");
                      setCustomType("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
              {errors.type && <span className="su-form-error">{errors.type}</span>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="su-btn-primary su-add-contact-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "ADDING..." : "ADD CONTACT"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddContactModal;


